<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import {
  MISMATCH_DATA_TYPE,
  TERMS_AND_CONDITIONS_LABEL,
  UNIQUE_KEY_FIELD_KEYS,
} from "@/util/constants";
import {
  CUSTOM_FIELD_NAME_TOOLTIP_LEARN_MORE_URL,
  getCustomFieldNameTooltipKey,
  getCustomFieldNameTooltipLearnMoreKey,
  getCustomObjectName,
  getCustomObjectNamePlural,
  isCustomObjectField,
  isPrimaryField,
  isTrueCustomObjectField,
} from "@/util/customObjectUtils";
import {
  isFileAccessLocked,
  isPrivateCustomField,
  supportsFileAccessChoice,
} from "@/util/customFieldPrivacy";
import { AlignmentPosition, applyStyles } from "@/util/methods";
import {
  isPictureChoiceField,
  isPictureChoiceSelectionTypeLocked,
  preparePictureChoiceElementUpdate,
} from "@/util/pictureChoice";
import type { UrlValidation } from "@/util/urlField";
// @ts-ignore
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  Edit01Icon,
  Monitor04Icon,
  Phone01Icon,
  PlusIcon,
  Trash01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
// @ts-ignore
import RichTextEditor from "@/components/text-editor/index.vue";
import { getDeviceAware, setDeviceAware } from "@/composition/useDeviceAware";
import { updateAllowCustomOptionInStore } from "@/composition/useOtherOption";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { deviceOptions } from "@/util/constants";
import {
  UIAlert,
  UIButton,
  UICheckbox,
  UICollapse,
  UICollapseItem,
  UIDivider,
  UIDropdown,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UISelect,
  UISwitch,
  UITabPane,
  UITabs,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { cloneDeep, snakeCase } from "lodash";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AddressSettings from "../common/Address/AddressSettings.vue";
import CodeEditorModel from "../common/CodeEditorModel.vue";
import ConditionalLogicModal from "../common/ConditionalLogicModal.vue";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CountInput from "../common/CountInput.vue";
import DateSettings from "../common/DateSettings.vue";
import DateTimeSettings from "../common/DateTimeSettings.vue";
import { useDateTimeFieldHandlers } from "@/composables/useDateTimeFieldHandlers";
import { isCustomDateTimeElement } from "@/util/dateTimeSerialize";
import DropdownEditor from "../common/DropdownEditor.vue";
import EditOpportunitySettingsModal from "../common/EditOpportunitySettingsModal.vue";
import RatingEditor from "../common/ElementEditors/RatingEditor.vue";
import CaptchaVerificationMode from "../common/Elements/CaptchaVerificationMode.vue";
import LabelAlignment from "../common/Elements/LabelAlignment.vue";
import FileUploadEditor from "../common/FileUploadEditor.vue";
import ListEditor from "../common/ListEditor.vue";
import PaymentSettings from "../common/Payments/PaymentSettings.vue";
import PictureChoiceAltTextSettings from "../common/PictureChoice/PictureChoiceAltTextSettings.vue";
import PictureChoiceSettings from "../common/PictureChoice/PictureChoiceSettings.vue";
import PopupRichTextEditor from "../common/PopupRichTextEditor.vue";
import RadioSelectEditor from "../common/RadioSelectEditor.vue";
import ScoreEditor from "../common/ScoreEditor.vue";
import MediaCenter from "../common/Sidebar/MediaCenter.vue";
import SurveyImageLayoutSettings from "../common/StyleSettings/SurveyImageLayoutSettings.vue";
import TextSidebar from "../common/TextSidebar.vue";
import UrlFieldAlert from "../common/UrlFieldAlert.vue";
import UrlFieldSettings from "../common/UrlFieldSettings.vue";
import Card from "./../common/CardComponent.vue";

const store = useAppStore();
const emitter = useEmitter();
const { t } = useI18n();
// Extracted complex condition used in template
const shouldShowMigrationWarning = computed(() => {
  if (isPictureChoiceField(selectedElementStore)) {
    return false;
  }
  const dt = selectedElementStore?.dataType;
  const isTermsAndConditions = selectedElementStore?.tag === "terms_and_conditions";
  const types = [
    "SINGLE_OPTIONS",
    "MULTIPLE_OPTIONS",
    "RADIO",
    "CHECKBOX",
    "FILE_UPLOAD",
    "SIGNATURE",
  ];
  const matches = (types as string[]).includes(dt as string) || isTermsAndConditions;
  return matches && (!store.builder.version || store.builder.version >= 4);
});
const { getCustomFieldsFromStore } = storeToRefs(store);
const props = defineProps({
  selectedElement: {
    type: Object,
    default() {
      return {};
    },
  },
});

const isMobile = computed(() => store.deviceMode === "mobile");
const mediaCenterSubCategory = computed(
  () => store.mediaCenterSubCategory ?? MediaCenterSubCategory.SURVEY
);

//Get Slide Settings or Slide Elements Options Settings
const selectedElementStore = reactive(
  props.selectedElement.index !== undefined
    ? store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    : store.slides[props.selectedElement.slideIndex]
);
const fileAccessLocked = computed(() =>
  isFileAccessLocked(selectedElementStore)
);

const updateFileAccess = (isPrivate: boolean) => {
  selectedElementStore.isPrivate = isPrivate;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const showPictureChoiceSettings = computed(() =>
  isPictureChoiceField(selectedElementStore)
);

const updatePictureChoiceElement = (updatedElement: any) => {
  const currentElement =
    store.slides[props.selectedElement.slideIndex]?.slideData?.[
      props.selectedElement.index
    ];
  if (!currentElement) return;

  const { nextElement, apiFieldsChanged } = preparePictureChoiceElementUpdate(
    currentElement,
    updatedElement
  );
  Object.assign(currentElement, nextElement);
  Object.assign(selectedElementStore, nextElement);
  if (
    apiFieldsChanged &&
    isPictureChoiceSelectionTypeLocked(currentElement)
  ) {
    currentElement.customfieldUpdated = true;
  }
  store.anyUnsavedChanges = true;
};

const dateElement = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index];
});

const dtHandlers = useDateTimeFieldHandlers(() => dateElement.value);
const isCustomDateTimeField = computed(() => isCustomDateTimeElement(dateElement.value));
const disableCollectType = computed(
  () => !!(dateElement.value?.customEdited || !dateElement.value?.custom)
);

const showModal = ref(false);
const uniqueKeyPlaceholder = ref(snakeCase(selectedElementStore?.label));
const uniqueKeyError = ref(false);
const customFieldError = ref(false);

const showModelUpdate = (modalUpdate: boolean) => {
  showModal.value = modalUpdate;
};

const submitUpdate = (updatedCode: string) => {
  selectedElementStore.html = updatedCode;
  store.anyUnsavedChanges = true;
};

const onClickOfEditHTML = () => {
  showModal.value = !showModal.value;
};

const onBack = () => {
  store.isAnyElementActive = false;
};

const switchDeviceMode = (option: string) => {
  store.deviceMode = option as any;
};

const openConditionalLogicFromField = () => {
  // Preselect filter by Field with current element's query key ONLY if >1 conditions exist for it
  const element =
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index];
  const queryKey = element?.hiddenFieldQueryKey;
  let shouldPrefilter = false;
  if (queryKey && Array.isArray((store as any)?.builderConfig?.conditionalLogic)) {
    const rules = (store as any).builderConfig.conditionalLogic as any[];
    const count = rules.reduce((acc, rule) => {
      if (!Array.isArray(rule?.conditions)) {
        return acc;
      }
      const hits = rule.conditions.filter((c: any) => c?.selectedField === queryKey).length;
      return acc + hits;
    }, 0);
    shouldPrefilter = count > 0;
  }
  if (queryKey && shouldPrefilter) {
    (store as any).conditionalLogicFilter = { mode: "field", values: [queryKey] };
  } else {
    (store as any).conditionalLogicFilter = undefined;
  }
  store.showConditionalLogicModal = true;
};

const getSlideName = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideName;
});

const getLabel = computed(() => {
  const el =
    store.slides[props.selectedElement.slideIndex].slideData[
      props.selectedElement.index
    ];
  return el?.label;
});

const getTypeLabel = computed(() => {
  const typeLabel =
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
      ?.typeLabel;
  if (typeLabel) {
    return typeLabel;
  }
  const model = selectedElementStore?.model;
  if (!model || model === "contact") {
    return "";
  }
  let displayName = "";
  if (model === "business") {
    displayName = t("common.business");
  } else if (model === "opportunity") {
    displayName = t("common.opportunity");
  } else if (isTrueCustomObjectFieldWrapper(model)) {
    displayName = getCustomObjectNamePluralWrapper(model);
  }
  if (!displayName) {
    const seg = (model.split(".").pop() || model) as string;
    displayName = seg.charAt(0).toUpperCase() + seg.slice(1);
  }
  if (typeof displayName === "string" && displayName.length > 0) {
    displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
  }
  const prefix = t("common.object") || "Object";
  return `${prefix}: ${displayName}`;
});

const getUniqueKey = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    ?.fieldKey;
});

const updatePicklistOptions = (options) => {
  const picklistOptionsCopy = cloneDeep(selectedElementStore.picklistOptions);
  if (
    JSON.stringify(picklistOptionsCopy) !== JSON.stringify(selectedElementStore.options) &&
    selectedElementStore?.id &&
    selectedElementStore?.logic
  ) {
    store.slides[props.selectedElement.slideIndex].logic[selectedElementStore?.id] = null;
  }
  if (
    ["SINGLE_OPTIONS", "MULTIPLE_OPTIONS", "RADIO", "CHECKBOX"].includes(
      selectedElementStore?.dataType
    )
  ) {
    // Always update calculatedOptions, even when calculations are disabled
    // This ensures values are preserved when toggling the calculations feature
    selectedElementStore.calculatedOptions = [...options];

    // Update picklistOptions with just the labels for backward compatibility
    selectedElementStore.picklistOptions = options.map((option: any) =>
      typeof option === "object" && option !== null ? option.label : option
    );
  } else {
    selectedElementStore.picklistOptions = options;
  }
  if (store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]?.id) {
    store.slides[props.selectedElement.slideIndex].slideData[
      props.selectedElement.index
    ].customfieldUpdated = true;
  }
  store.anyUnsavedChanges = true;
};

const updateStyle = (style) => {
  if (selectedElementStore) {
    selectedElementStore.style = style;
    store.anyUnsavedChanges = true;
  }
};
const updateAllowCustomOption = (value: boolean) => {
  updateAllowCustomOptionInStore(
    store,
    selectedElementStore,
    value,
    t("common.otherOptionPlaceholder")
  );
};

const updateEnableCalculations = (newValue) => {
  // If disabling calculations, save a backup of the current calculation values
  if (!newValue && selectedElementStore.calculatedOptions) {
    // Create a backup of calculation values before disabling
    selectedElementStore._calculatedOptionsBackup = cloneDeep(
      selectedElementStore.calculatedOptions
    );
  }

  // Update enabled state
  selectedElementStore.enableCalculations = newValue;

  // If enabling calculations, restore from backup or recreate from scratch
  if (newValue) {
    const currentOptions = selectedElementStore.picklistOptions || [];

    if (selectedElementStore._calculatedOptionsBackup) {
      // Create a map of label -> calculatedValue from backup
      const valueMap = {};
      selectedElementStore._calculatedOptionsBackup.forEach((option) => {
        if (typeof option === "object" && option.label) {
          valueMap[option.label] = option.calculatedValue;
        }
      });

      // Rebuild calculatedOptions from current options, preserving calculation values
      selectedElementStore.calculatedOptions = currentOptions.map((option) => {
        const label = typeof option === "object" ? option.label : option;
        const calculatedValue = valueMap[label] || "";
        return { label, calculatedValue };
      });
    } else {
      // No backup available, create fresh ones
      selectedElementStore.calculatedOptions = currentOptions.map((option) => {
        const label = typeof option === "object" ? option.label : option;
        return { label, calculatedValue: "" };
      });
    }
  }

  store.anyUnsavedChanges = true;
};

const checkUniqueKeyExists = (
  key: string,
  currentSlideIndex?: number,
  currentElementIndex?: number
) => {
  // Check if the field is standard
  if (store.slides[currentSlideIndex]?.slideData[currentElementIndex]?.standard) {
    return false;
  }
  // Check in store.customFields
  const isUniqueExistsInCustomFields = store.customFields.some((customField) => {
    const uniqueKey = customField.fieldKey?.split(".")?.[1];
    return uniqueKey === key || customField.fieldKey === key;
  });

  //Check in all slides and their elements (excluding current element if excludeIndex is provided)
  const isUniqueExistsInElements = store.slides.some((slide, slideIndex) => {
    return slide.slideData.some((element, elementIndex) => {
      if (
        currentSlideIndex !== undefined &&
        currentElementIndex !== undefined &&
        slideIndex === currentSlideIndex &&
        elementIndex === currentElementIndex
      ) {
        return false;
      }
      return element.fieldKey === key;
    });
  });

  return isUniqueExistsInCustomFields || isUniqueExistsInElements;
};

const updateUniqueKey = (value: string) => {
  if (value.trim() !== "") {
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].fieldKey =
      value;
    store.anyUnsavedChanges = true;
    uniqueKeyError.value = checkUniqueKeyExists(
      value,
      props.selectedElement.slideIndex,
      props.selectedElement.index
    );
  }
};

const checkUniqueCustomFieldNameExists = (
  name: string,
  currentSlideIndex?: number,
  currentElementIndex?: number
) => {
  // Check if the field is standard
  if (store.slides[currentSlideIndex]?.slideData[currentElementIndex]?.standard) {
    return false;
  }
  const isCustomFieldNameExistsInCustomFields = store.customFields.some((customField) => {
    return customField.name === name || customField.label === name;
  });

  const isCustomFieldNameExistsInSlides = store.slides.some((slide, slideIndex) => {
    return slide.slideData.some((element, elementIndex) => {
      if (
        currentSlideIndex !== undefined &&
        currentElementIndex !== undefined &&
        slideIndex === currentSlideIndex &&
        elementIndex === currentElementIndex
      ) {
        return false;
      }
      return element.name === name || element.label === name;
    });
  });

  return isCustomFieldNameExistsInCustomFields || isCustomFieldNameExistsInSlides;
};

const getPlaceholder = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .placeholder;
});

const getShortLabel = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .shortLabel;
});

const getHiddenQuery = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .hiddenFieldQueryKey;
});

const getRequired = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .required;
});

const getHidden = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .hidden;
});

const getEnableCountryPicker = computed(() => {
  if (
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
      .enableCountryPicker === undefined
  ) {
    return true;
  }
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .enableCountryPicker;
});

const enableValidateEmailModel = computed({
  get() {
    return (
      store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
        ?.enableValidateEmail ?? false
    );
  },
  set(value: boolean) {
    store.slides[props.selectedElement.slideIndex].slideData[
      props.selectedElement.index
    ].enableValidateEmail = value;
    store.anyUnsavedChanges = true;
  },
});

const enableValidatePhoneModel = computed({
  get() {
    return (
      store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
        ?.enableValidatePhone ?? false
    );
  },
  set(value: boolean) {
    store.slides[props.selectedElement.slideIndex].slideData[
      props.selectedElement.index
    ].enableValidatePhone = value;
    store.anyUnsavedChanges = true;
  },
});

const getHiddenValue = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .hiddenFieldValue;
});

const getSource = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    .value;
});

const getSpreadColumns = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    ?.spreadColumns;
});
const getNumberOfColumns = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    ?.columnsNumber;
});

const getDefaultNumberOfColumns = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    ?.picklistOptionsImage
    ? undefined
    : 1;
});

const isHiddenFieldDisable = computed(() => {
  const element =
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index];
  if (!element) {
    return false;
  }
  if (element?.required) {
    return true;
  }
  // Disable hidden for primary fields
  const primaryInfo = isPrimaryField(element, store.customObjects || []);
  return Boolean(primaryInfo?.isPrimary);
});

const isRequiredFieldDisable = computed(() =>
  Boolean(
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]?.hidden
  )
);

// Disable Required checkbox only when field is hidden or a primary field
const isRequiredCheckboxDisabled = computed(() => {
  const element =
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index];
  const baseDisabled = isRequiredFieldDisable.value; // disabled when hidden
  if (!element) {
    return baseDisabled;
  }
  // Lock required for primary fields; allow toggling otherwise
  const primaryInfo = isPrimaryField(element, store.customObjects || []);
  const isPrimary = Boolean(primaryInfo?.isPrimary);
  return baseDisabled || isPrimary;
});

const pickupFieldOptions = computed(() => {
  if (selectedElementStore.picklistOptions === undefined) {
    return selectedElementStore.picklistOptionsImage;
  }
  return selectedElementStore.picklistOptions;
});

const disableUniqueKey = computed(() => {
  return (
    selectedElementStore?.customEdited ||
    !selectedElementStore?.custom ||
    selectedElementStore?.dataType === "SCORE" ||
    selectedElementStore?.dataType === MISMATCH_DATA_TYPE.RATING
  );
});

const setSlideJumpTo = (value) => {
  store.slides[props.selectedElement.slideIndex].jumpTo = value;
  store.anyUnsavedChanges = true;
  try {
    // Keep Conditional Logic (Jump To - Slide basis) in sync with sidebar selection
    const originSlideId = store.slides[props.selectedElement.slideIndex].id;
    if (!(store as any).builderConfig) {
      (store as any).builderConfig = { conditionalLogic: [] } as any;
    }
    if (!Array.isArray((store as any).builderConfig.conditionalLogic)) {
      (store as any).builderConfig.conditionalLogic = [];
    }
    const rules = (store as any).builderConfig.conditionalLogic as any[];
    const existingIndex = rules.findIndex(
      (r) =>
        r?.outcome?.type === "jumpTo" &&
        Array.isArray(r?.conditions) &&
        r.conditions.length === 1 &&
        r.conditions[0]?.selectedField === originSlideId
    );

    if (value) {
      const jumpRule = {
        conditions: [
          {
            selectedField: originSlideId,
            selectedOperation: "isEqualTo",
            inputValue: null,
          },
        ],
        outcome: { type: "jumpTo", value: "", jumpTarget: value, jumpTargetType: "slide" },
        conditionalOperation: "then",
      } as any;
      if (existingIndex >= 0) {
        // Update target only; keep rule order stable
        rules[existingIndex].outcome = jumpRule.outcome;
      } else {
        rules.push(jumpRule);
      }
    } else if (existingIndex >= 0) {
      // If cleared, remove the matching rule
      rules.splice(existingIndex, 1);
    }
  } catch (e) {
    // no-op safeguard; avoid blocking sidebar interactions
  }
};

const radioOptionSelection = (value, option, optionIndex) => {
  const selectedKey = selectedElementStore.id;
  const logic = store.slides[props.selectedElement.slideIndex].logic || {};
  if (value !== "") {
    if (logic[selectedKey]) {
      logic[selectedKey]["option_" + optionIndex] = {
        name: [-1, -2].includes(value) ? disqualifyName(value) : getName(value),
        id: [-1, -2].includes(value) ? disqualifyId(value) : value,
        index: [-1, -2].includes(value) ? value : slideAndFieldsMapping.value[value],
        fieldId: selectedKey,
        optionIndex: optionIndex,
        optionName: option.label ? option.label : option,
        status: true,
      };
    } else {
      logic[selectedKey] = {
        ["option_" + optionIndex]: {
          name: [-1, -2].includes(value) ? disqualifyName(value) : getName(value),
          id: [-1, -2].includes(value) ? disqualifyId(value) : value,
          index: [-1, -2].includes(value) ? value : slideAndFieldsMapping.value[value],
          fieldId: selectedKey,
          optionIndex: optionIndex,
          optionName: option.label ? option.label : option,
          status: true,
        },
      };
    }
  } else if (logic[selectedKey]) {
    if (logic[selectedKey]["option_" + optionIndex]) {
      delete logic[selectedKey]["option_" + optionIndex];
    }
    if (Object.keys(logic[selectedKey]).length <= 0) {
      delete logic[selectedKey];
    }
  }
  if (Object.keys(logic).length <= 0) {
    delete store.slides[props.selectedElement.slideIndex].logic;
  } else {
    store.slides[props.selectedElement.slideIndex].logic = logic;
  }
  store.anyUnsavedChanges = true;
};

const getName = (index) => {
  return radioOptionMapping.value[index].label;
};

const slideOptions = computed(() => {
  if (props.selectedElement.slideIndex === store.slides.length - 1) {
    return [{ value: "submitSurvey", label: t("common.submitSurvey") }];
  }
  return store.getSlideDropDownMenu(props.selectedElement.slideIndex);
});

const radioOption = computed(() => {
  const radioOptionTemp: any = [{ value: "", label: t("common.select") }];
  store.slides.forEach((slide, index) => {
    if (props.selectedElement.slideIndex !== index) {
      radioOptionTemp.push({
        value: slide.id,
        label: `P${index + 1} - ${slide.slideName}`,
      });
    }
  });

  radioOptionTemp.push({
    value: -1,
    label: t("common.disqualifyImmediately"),
  });
  radioOptionTemp.push({
    value: -2,
    label: t("common.disqualifyAfterSubmit"),
  });
  return radioOptionTemp;
});

const radioOptionMapping = computed(() => {
  const mapping = {};
  radioOption.value.forEach((current) => {
    if (!mapping[current.value]) {
      mapping[current.value] = current;
    }
  });
  return mapping;
});

const slideAndFieldsMapping = computed(() => {
  const mapping: { [key: string]: number } = {};
  if (!store.formAction.fieldSettingEnable) {
    store.slides.forEach((slide, index) => {
      if (!mapping[slide.id]) {
        mapping[slide.id] = index;
      }
    });
  } else {
    store.slides[0].slideData.forEach((element, index) => {
      if (!mapping[element.tag]) {
        mapping[element.tag] = index;
      }
    });
  }
  return mapping;
});

const fieldRadioActionModel = (index) => {
  const elementId = selectedElementStore.id;

  const logic = store.slides[props.selectedElement.slideIndex].logic;
  if (logic && logic[elementId] && logic[elementId]["option_" + index]) {
    return [-1, -2].includes(logic[elementId]["option_" + index].index)
      ? logic[elementId]["option_" + index].index
      : logic[elementId]["option_" + index].id;
  }
  return "";
};

const disqualifyName = (value) => {
  return value === -1 ? t("common.disqualifyImmediately") : t("common.disqualifyAfterSubmitLower");
};
const disqualifyId = (value) => {
  return value === -1 ? "disqualify" : "endsurvey";
};

const handleImageWidth = (val) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].width = val;
  store.anyUnsavedChanges = true;
};

const handleImageHeight = (val) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].height =
    val;
  store.anyUnsavedChanges = true;
};

const handleTextColor = (val: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].textColor =
    val;
  store.anyUnsavedChanges = true;
};

const handleLinkColor = (val: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].linkColor =
    val;
  store.anyUnsavedChanges = true;
};

const getAltImage = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].alt;
});

const alignImage = (align) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].align =
    align;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};

const updatePreview = (element) => {
  const content = element.placeholder2
    ? `${element.placeholder};${element.placeholder2}`
    : `${element.placeholder}`;
  element.preview = applyStyles(content, element.linkColor, element.textColor);
};

const updateCalculation = (calculation) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].calculation = calculation;
  store.anyUnsavedChanges = true;
};

const saveOpportunitySettings = (settings) => {
  store.opportunitySettings = settings;
  store.anyUnsavedChanges = true;
  store.showOpportunitiesModal = false;
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

// URL for custom field tooltip learn more link (same for all field types)
const customFieldNameTooltipLearnMoreUrl = CUSTOM_FIELD_NAME_TOOLTIP_LEARN_MORE_URL;

// Computed properties for custom field name tooltip
const customFieldNameTooltipKey = computed(() => {
  const model = selectedElementStore?.model;
  return getCustomFieldNameTooltipKey(model);
});

const customFieldNameTooltipLearnMoreKey = computed(() => {
  const model = selectedElementStore?.model;
  return getCustomFieldNameTooltipLearnMoreKey(model);
});

/**
 * Applies URL validation settings to the selected survey field.
 *
 * @param urlValidation - Normalized URL validation settings.
 */
const updateUrlValidation = (urlValidation: UrlValidation) => {
  if (!selectedElementStore) return;

  selectedElementStore.urlValidation = urlValidation;
  if (selectedElementStore.id) selectedElementStore.customfieldUpdated = true;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

// Wrapper functions for template access to utilities
const isCustomObjectFieldWrapper = (model: string) => isCustomObjectField(model);
const isTrueCustomObjectFieldWrapper = (model: string) => isTrueCustomObjectField(model);
const getCustomObjectNameWrapper = (model: string) =>
  getCustomObjectName(model, store.customObjects || []);
const getCustomObjectNamePluralWrapper = (model: string) =>
  getCustomObjectNamePlural(model, store.customObjects || []);

const tncElement =
  store.slides[props.selectedElement.slideIndex]?.slideData?.[props.selectedElement.index];

watch(
  () => [
    tncElement?.placeholder,
    tncElement?.mobilePlaceholder,
    tncElement?.placeholder2,
    tncElement?.linkColor,
    tncElement?.textColor,
  ],
  () => {
    updatePreview(tncElement);
  }
);

const showPopupRichTextEditorModal = ref(false);

const showPopupRichTextEditorUpdate = (modalUpdate: boolean) => {
  showPopupRichTextEditorModal.value = modalUpdate;
};

const submitPopupRichTextEditorUpdate = (updatedCode: string) => {
  selectedElementStore.description = updatedCode;
  store.anyUnsavedChanges = true;
};

const editDescription = () => {
  showPopupRichTextEditorModal.value = !showPopupRichTextEditorModal.value;
};

//To Maintain Reactivity of Store
const handleTextUpdate = (output) => {
  Object.assign(
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index],
    output
  );
};

const updateSlideName = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideName = value;
  store.anyUnsavedChanges = true;
};

const updateLabel = (value: string) => {
  uniqueKeyPlaceholder.value = snakeCase(value);
  if (
    selectedElementStore?.dataType === "SCORE" &&
    !uniqueKeyPlaceholder.value.startsWith("score_")
  ) {
    uniqueKeyPlaceholder.value = "score_" + uniqueKeyPlaceholder.value;
  }
  if (
    selectedElementStore?.dataType === MISMATCH_DATA_TYPE.RATING &&
    !uniqueKeyPlaceholder.value.startsWith("rating_" + UNIQUE_KEY_FIELD_KEYS.RATING)
  ) {
    uniqueKeyPlaceholder.value =
      "rating_" + UNIQUE_KEY_FIELD_KEYS.RATING + "_" + uniqueKeyPlaceholder.value;
  }

  if (!selectedElementStore?.id) {
    updateUniqueKey(uniqueKeyPlaceholder.value);
    customFieldError.value = checkUniqueCustomFieldNameExists(
      value,
      props.selectedElement.slideIndex,
      props.selectedElement.index
    );
  }
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].label =
    value;
  store.anyUnsavedChanges = true;
};

const onCaptchaInvisibleChange = (value: boolean) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].invisible = value;
  store.addToHistory();
  store.anyUnsavedChanges = true;
};

const handleLabelAlignment = (value: AlignmentPosition) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].labelAlignment = value;
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].placeholder = value;
  store.anyUnsavedChanges = true;
};

const getPlaceholder2 = computed(() => {
  return store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
    ?.placeholder2;
});

const updatePlaceholder2 = (value: string): void => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].placeholder2 = value;
  store.anyUnsavedChanges = true;
};

// Default content style for T&C label
const tncLabelContentStyle = computed(() => {
  let color = TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR;
  if (isMobile.value && store.fieldStyle?.mobileLabelColor) {
    color = "#" + String(store.fieldStyle?.mobileLabelColor);
  } else if (store.fieldStyle?.labelColor) {
    color = "#" + String(store.fieldStyle?.labelColor);
  }

  return { color };
});

const addSecondTnc = () => {
  if (
    !store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
      .placeholder2
  ) {
    store.slides[props.selectedElement.slideIndex].slideData[
      props.selectedElement.index
    ].placeholder2 = `<p style='font-family: Roboto; font-size: 14px; font-weight: 400; color: ${TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR};'></p>`;
    store.anyUnsavedChanges = true;
  }
};

const removeSecondTnc = () => {
  if (
    store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index]
      .placeholder2
  ) {
    store.slides[props.selectedElement.slideIndex].slideData[
      props.selectedElement.index
    ].placeholder2 = undefined;
    store.anyUnsavedChanges = true;
  }
};

const updateShortLabel = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].shortLabel =
    value;
  store.anyUnsavedChanges = true;
};

const updateHiddenQuery = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].hiddenFieldQueryKey = value;
  store.anyUnsavedChanges = true;
};

const updateRequired = (value: boolean) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].required =
    value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateHidden = (value: boolean) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].hidden =
    value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateEnableCountryPicker = (value: boolean) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].enableCountryPicker = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateHiddenValue = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].hiddenFieldValue = value;
  store.anyUnsavedChanges = true;
};

const updateSource = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].value =
    value;
  store.anyUnsavedChanges = true;
};

const updateAltImage = (value: string) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index].alt = value;
  store.anyUnsavedChanges = true;
};

const updateDateFormat = (value: string) => {
  dtHandlers.updateDateFormat(value);
};
const updateDateSeparator = (value: string) => {
  dtHandlers.updateDateSeparator(value);
};

const updateDatedisablePicker = (value: boolean) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].disablePicker = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateSpreadColumns = (value: boolean) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].spreadColumns = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};
const updateNumberOfColumns = (value: number) => {
  store.slides[props.selectedElement.slideIndex].slideData[
    props.selectedElement.index
  ].columnsNumber = value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const handleRatingConfig = (key, value) => {
  store.slides[props.selectedElement.slideIndex].slideData[props.selectedElement.index][key] =
    value;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const checkInitialValidation = () => {
  if (!selectedElementStore?.id) {
    const currentFieldKey = selectedElementStore?.fieldKey;

    if (currentFieldKey) {
      uniqueKeyError.value = currentFieldKey
        ? checkUniqueKeyExists(
            currentFieldKey,
            props.selectedElement.slideIndex,
            props.selectedElement.index
          )
        : false;
    }
    const currentCustomFieldName = selectedElementStore?.name || selectedElementStore?.label;

    customFieldError.value = checkUniqueCustomFieldNameExists(
      currentCustomFieldName,
      props.selectedElement.slideIndex,
      props.selectedElement.index
    );
  }
};

onMounted(() => {
  checkInitialValidation();
});

// ---------------------------------------------
// Sidebar State Management (open/close/restore)
// ---------------------------------------------

const getSidebarState = (callback: (state: Record<string, boolean>) => void): void => {
  if (typeof callback === "function") {
    callback({ surveyCustomSidebarOpen: store.isAnyElementActive && !!store.surveyId });
  }
};

const getSurveyCustomSidebarState = (callback: (state: boolean) => void): void => {
  if (typeof callback === "function") {
    callback(store.isAnyElementActive && !!store.surveyId);
  }
};

const closeAllSidebars = (): void => {
  if (store.isAnyElementActive) {
    store.isAnyElementActive = false;
  }
};

const restoreSidebarState = (state: Record<string, boolean>): void => {
  if (state && typeof state.surveyCustomSidebarOpen === "boolean") {
    store.isAnyElementActive = state.surveyCustomSidebarOpen;
  }
};

onBeforeMount(() => {
  emitter.on("get-sidebar-state", getSidebarState);
  emitter.on("get-survey-custom-sidebar-state", getSurveyCustomSidebarState);
  emitter.on("close-all-sidebars", closeAllSidebars);
  emitter.on("restore-sidebar-state", restoreSidebarState);
});

onBeforeUnmount(() => {
  emitter.off("get-sidebar-state", getSidebarState);
  emitter.off("get-survey-custom-sidebar-state", getSurveyCustomSidebarState);
  emitter.off("close-all-sidebars", closeAllSidebars);
  emitter.off("restore-sidebar-state", restoreSidebarState);
});
</script>

<template>
  <div>
    <div
      class="relative flex items-center justify-center"
      :class="isCustomDateTimeField ? 'mb-2' : 'mb-6'"
    >
      <button
        type="button"
        class="arrow-button absolute left-0 border-0 bg-transparent p-0"
        :aria-label="t('common.back')"
        @click="onBack"
      >
        <ArrowLeftIcon class="mr-2 h-4 w-4 text-gray-800" />
      </button>
      <div class="text-md text-gray-900">{{ getTypeLabel }}</div>
    </div>
    <div
      :class="{ 'date-time-sidebar-tabs': isCustomDateTimeField }"
      :id="
        showPictureChoiceSettings ||
        ([
          'TEXTBOX_LIST',
          'SINGLE_OPTIONS',
          'MULTIPLE_OPTIONS',
          'RADIO',
          'CHECKBOX',
          'FILE_UPLOAD',
          'SCORE',
        ].includes(selectedElementStore?.dataType) &&
          !selectedElementStore?.picklistOptionsImage?.length)
          ? ''
          : 'hide-tabs'
      "
    >
      <UITabs justify-content="space-around" default-value="Content" type="line">
        <UITabPane name="Content" :tab="t('common.content')">
          <AddressSettings
            v-if="selectedElementStore?.tag === 'group_address'"
            :address="selectedElementStore"
            :selected-index="props.selectedElement.index"
            :selected-slide-index="props.selectedElement.slideIndex"
          ></AddressSettings>
          <DateTimeSettings
            v-else-if="isCustomDateTimeField"
            :date-element="dateElement"
            :is-required-field-disable="isRequiredCheckboxDisabled"
            :is-hidden-field-disable="isHiddenFieldDisable"
            :is-custom-field-error="customFieldError"
            :disable-collect-type="disableCollectType"
            @update-label="updateLabel"
            @update-placeholder="updatePlaceholder"
            @update-short-label="updateShortLabel"
            @update-hidden-query="updateHiddenQuery"
            @update-required="updateRequired"
            @update-hidden="updateHidden"
            @update-hidden-value="updateHiddenValue"
            @update-date-format="updateDateFormat"
            @update-date-separator="updateDateSeparator"
            @update-date-disable-picker="updateDatedisablePicker"
            @update-label-alignment="handleLabelAlignment"
            @update-collect-type="dtHandlers.updateCollectType"
            @update-date-mode="dtHandlers.updateDateMode"
            @update-future-sub="dtHandlers.updateFutureSub"
            @update-buffer="dtHandlers.updateBuffer"
            @update-rolling="dtHandlers.updateRolling"
            @update-date-range="dtHandlers.updateDateRange"
            @update-days="dtHandlers.updateDays"
            @update-time-mode="dtHandlers.updateTimeMode"
            @update-time-range="dtHandlers.updateTimeRange"
            @update-time-format="dtHandlers.updateTimeFormat"
            @update-default-time="dtHandlers.updateDefaultTime"
            @update-custom-time="dtHandlers.updateCustomTime"
          >
            <template #advanced>
              <UICollapse
                v-if="!selectedElementStore?.standard && selectedElement.index !== undefined"
                id="date-advanced-settings"
                :arrow-placement="'left'"
                :default-expanded-names="[t('common.advancedSettings')]"
              >
                <UICollapseItem
                  id="advanced-settings"
                  :title="t('common.advancedSettings')"
                  :name="t('common.advancedSettings')"
                >
                  <div>
                    <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                      {{ t("common.customFieldName") }}
                      <UITooltip trigger="hover" placement="top">
                        <template #trigger>
                          <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                        </template>
                        <template #default>
                          <span>
<!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -- pre-existing model-driven tooltip key, unrelated to date-time field -->
                            {{ t(customFieldNameTooltipKey) }}
                            <a
                              :href="customFieldNameTooltipLearnMoreUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="text-blue-500 underline hover:text-blue-700"
                            >
<!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -- pre-existing model-driven tooltip key, unrelated to date-time field -->
                              {{ t(customFieldNameTooltipLearnMoreKey) }}
                            </a>
                          </span>
                        </template>
                      </UITooltip>
                    </div>
                    <UIInput
                      id="custom-field-label"
                      class="standard-input-ghl"
                      :model-value="
                        selectedElementStore?.customFieldLabel ||
                        selectedElementStore?.name ||
                        selectedElementStore?.label
                      "
                      :disabled="selectedElementStore?.customEdited || !selectedElementStore?.custom"
                      :status="customFieldError ? 'error' : undefined"
                      @update:model-value="updateLabel"
                      @blur="store.addToHistory()"
                    />
                    <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                      {{ t("common.customFieldExistsError") }}
                    </div>
                    <div class="switch-handle mb-1 mt-6 block text-sm font-medium text-gray-700">
                      {{ t("common.uniqueKey") }}
                      <UITooltip trigger="hover" placement="top">
                        <template #trigger>
                          <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                        </template>
                        <template #default>
                          <span>
                            {{ t("common.uniqueKeyTooltip") }}
                            <a
                              :href="customFieldNameTooltipLearnMoreUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="ml-1 text-blue-500 underline hover:text-blue-700"
                            >
                              {{ t("common.uniqueKeyTooltipLearnMore") }}
                            </a>
                          </span>
                        </template>
                      </UITooltip>
                    </div>
                    <UIInput
                      id="uniqueKey"
                      class="standard-input-ghl"
                      :model-value="getUniqueKey"
                      :disabled="disableUniqueKey"
                      :placeholder="uniqueKeyPlaceholder"
                      :status="uniqueKeyError ? 'error' : undefined"
                      @update:model-value="updateUniqueKey"
                      @blur="store.addToHistory()"
                    />
                    <div v-if="uniqueKeyError" class="mt-1 text-xs text-red-500">
                      {{ t("common.uniqueKeyNotUniqueError") }}
                    </div>
                  </div>
                </UICollapseItem>
              </UICollapse>
            </template>
          </DateTimeSettings>
          <UICollapse
            v-else
            id="custom-field-settings"
            :arrow-placement="'left'"
            :default-expanded-names="
              selectedElementStore?.dataType === 'URL'
                ? [
                    t('common.generalSettings'),
                    t('common.urlField.settingsTitle'),
                  ]
                : [t('common.generalSettings')]
            "
          >
            <RatingEditor
              v-if="selectedElementStore?.type === 'rating'"
              :product="store.product"
              :rating-element="selectedElementStore"
              :index="selectedElement.index"
              :is-required-field-disable="isRequiredCheckboxDisabled"
              :is-hidden-field-disable="isHiddenFieldDisable"
              :is-form="true"
              @update-label="updateLabel"
              @update-short-label="updateShortLabel"
              @update-required="updateRequired"
              @update-rating-count="handleRatingConfig('count', $event)"
              @update-icon-selected-state="handleRatingConfig('color', $event)"
              @update-lowest-rating="handleRatingConfig('lowestRating', $event)"
              @update-highest-rating="handleRatingConfig('highestRating', $event)"
              @update-icon-selected="handleRatingConfig('iconType', $event)"
              @update-icon-alignment="handleRatingConfig('iconAlignment', $event)"
              @update-calculation-type="handleRatingConfig('calculationType', $event)"
              @update-icon-unselected-state="handleRatingConfig('inactiveColor', $event)"
              @update-label-alignment="handleLabelAlignment"
            ></RatingEditor>
            <UICollapseItem
              v-else
              id="general-settings"
              :title="t('common.generalSettings')"
              :name="t('common.generalSettings')"
            >
              <template #header>
                <div class="flex w-full items-center justify-between">
                  <div>{{ t("common.generalSettings") }}</div>
                  <div
                    v-if="['header', 'terms_and_conditions'].includes(selectedElementStore?.tag)"
                    @click.stop
                  >
                    <UIDropdown
                      id="general-device-toggle"
                      :options="deviceOptions"
                      @select="switchDeviceMode"
                    >
                      <template #default>
                        <div class="flex items-center gap-0.5">
                          <Phone01Icon
                            v-if="store.deviceMode === 'mobile'"
                            class="h-4 w-4 cursor-pointer"
                          />
                          <Monitor04Icon v-else class="h-4 w-4 cursor-pointer" />
                          <ChevronDownIcon class="h-3 w-3" />
                        </div>
                      </template>
                    </UIDropdown>
                  </div>
                </div>
              </template>
              <UrlFieldAlert v-if="selectedElementStore?.dataType === 'URL'" />
              <div v-if="selectedElementStore?.type == 'html'">
                <div class="field-name-sidebar">
                  <UIInput
                    id="htmlLabelInline"
                    class="inline-label w-full bg-transparent p-0 text-base font-semibold shadow-none outline-none"
                    :model-value="getLabel"
                    :status="customFieldError ? 'error' : undefined"
                    @update:model-value="updateLabel"
                    @blur="store.addToHistory()"
                  />
                  <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                    {{ t("common.customFieldExistsError") }}
                  </div>
                </div>
                <div class="form-group">
                  <Card :header="selectedElementStore.placeholder">
                    <template #default>
                      <UIButton
                        id="editHtml"
                        type="default"
                        class="w-[100%]"
                        @click="onClickOfEditHTML"
                        >{{ t("common.editHTML") }}</UIButton
                      >
                    </template>
                  </Card>
                </div>
                <div>
                  <CodeEditorModel
                    v-if="showModal"
                    :model-active="showModal"
                    :code="
                      store.slides[props.selectedElement.slideIndex].slideData[
                        props.selectedElement.index
                      ].html
                    "
                    @update:show-model="showModelUpdate"
                    @update:submit="submitUpdate"
                  />
                </div>
              </div>
              <div v-else-if="selectedElementStore.tag === 'header'">
                <TextSidebar
                  :property="
                    JSON.parse(
                      JSON.stringify(
                        store.slides[props.selectedElement.slideIndex].slideData[
                          props.selectedElement.index
                        ]
                      )
                    )
                  "
                  @update:text="handleTextUpdate"
                />
              </div>
              <div v-else-if="selectedElementStore.tag === 'source'">
                <h6 class="field-name-sidebar">{{ selectedElementStore.label }}</h6>
                <div class="form-group">
                  <UIInput
                    id="sourceLabel"
                    class="standard-input-ghl"
                    :placeholder="t('common.enterSourceValue')"
                    :model-value="getSource"
                    @update:model-value="updateSource"
                    @blur="store.addToHistory()"
                  ></UIInput>
                </div>
              </div>
              <div v-else-if="selectedElementStore.tag === 'captcha'">
                <h6 class="field-name-sidebar">
                  {{ t("fields.customized.botProtection") }}
                </h6>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.label") }}</span
                    >
                  </div>
                  <UIInput
                    id="captchaLabel"
                    class="standard-input-ghl"
                    :model-value="getLabel"
                    :status="customFieldError ? 'error' : undefined"
                    @update:model-value="updateLabel"
                    @blur="store.addToHistory()"
                  >
                  </UIInput>
                  <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                    {{ t("common.customFieldExistsError") }}
                  </div>
                </div>
                <div class="form-group mt-4">
                  <LabelAlignment
                    :is-element-level="true"
                    :label-alignment="selectedElementStore.labelAlignment"
                    @update:label-alignment="handleLabelAlignment"
                  ></LabelAlignment>
                </div>
                <div class="form-group">
                  <CaptchaVerificationMode
                    :invisible="!!selectedElementStore?.invisible"
                    @update:invisible="onCaptchaInvisibleChange"
                  />
                </div>
              </div>
              <div v-else-if="selectedElementStore.tag === 'image'">
                <div class="field-name-sidebar">
                  <UIInput
                    id="imageLabelInline"
                    class="inline-label w-full bg-transparent p-0 text-base font-semibold shadow-none outline-none"
                    :model-value="getLabel"
                    :status="customFieldError ? 'error' : undefined"
                    @update:model-value="updateLabel"
                    @blur="store.addToHistory()"
                  />
                  <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                    {{ t("common.customFieldExistsError") }}
                  </div>
                </div>

                <MediaCenter
                  v-model:value="selectedElementStore.url"
                  :sub-category="MediaCenterSubCategory.SURVEY"
                />

                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.altText") }}</span
                    >
                  </div>
                  <UIInput
                    id="altText"
                    class="standard-input-ghl"
                    :model-value="getAltImage"
                    @update:model-value="updateAltImage"
                    @blur="store.addToHistory()"
                  ></UIInput>
                </div>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <div class="alignment">
                      <div class="alignment-div">{{ t("common.alignment") }}</div>
                      <div class="icons-collection">
                        <AlignLeftIcon
                          class="custom-icons h-5 w-5"
                          @click="alignImage('left')"
                        ></AlignLeftIcon>
                        <AlignCenterIcon
                          class="custom-icons h-5 w-5"
                          @click="alignImage('center')"
                        ></AlignCenterIcon>
                        <AlignRightIcon
                          class="custom-icons h-5 w-5"
                          @click="alignImage('right')"
                        ></AlignRightIcon>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <CountInput
                      :label="t('common.imageWidth')"
                      :value="selectedElementStore.width || ''"
                      :max="1800"
                      @update:value="handleImageWidth"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <CountInput
                      :label="t('common.imageHeight')"
                      :value="selectedElementStore.height || ''"
                      :max="1800"
                      @update:value="handleImageHeight"
                    />
                  </div>
                </div>
              </div>
              <div v-else-if="selectedElementStore.tag === 'terms_and_conditions'">
                <h6 class="field-name-sidebar">
                  {{ selectedElementStore.label || selectedElementStore.name }}
                </h6>
                <div class="form-group flex flex-col gap-y-4">
                  <div>
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.tnc1") }}</span
                    >
                    <div>
                      <!-- First TNC Editor with device-aware support -->
                      <div class="mb-4">
                        <RichTextEditor
                          :is-submit-message="true"
                          :content-style="tncLabelContentStyle"
                          :model-value="
                            getDeviceAware(
                              selectedElementStore,
                              'placeholder',
                              'mobilePlaceholder',
                              isMobile
                            )
                          "
                          @update:model-value="
                            (value) => {
                              setDeviceAware(
                                selectedElementStore,
                                'placeholder',
                                'mobilePlaceholder',
                                value,
                                isMobile
                              )
                              store.anyUnsavedChanges = true
                            }
                          "
                        />
                      </div>

                      <!-- Add/Remove TNC buttons -->
                      <div class="mb-4 flex justify-end space-x-2">
                        <UIButton
                          v-if="!selectedElementStore?.placeholder2"
                          id="add-second-tnc"
                          type="default"
                          size="small"
                          @click="addSecondTnc"
                        >
                          <PlusIcon class="mr-1.5 h-5 w-5" /> {{ t("common.addAnotherTnc") }}
                        </UIButton>
                      </div>

                      <!-- Second TNC Editor -->
                      <div v-if="selectedElementStore?.placeholder2" class="mb-1">
                        <div class="flex items-center justify-between">
                          <span class="hl-text-input-label block text-sm font-medium text-gray-700">
                            {{ t("common.tnc2") }}
                          </span>
                          <UIButton
                            id="remove-second-tnc-inline"
                            type="danger"
                            size="small"
                            @click="removeSecondTnc"
                          >
                            <Trash01Icon class="h-5 w-5" />
                          </UIButton>
                        </div>
                      </div>
                      <div v-if="selectedElementStore?.placeholder2" class="mb-4">
                        <RichTextEditor
                          :is-submit-message="true"
                          :content-style="tncLabelContentStyle"
                          :model-value="getPlaceholder2"
                          @update:model-value="updatePlaceholder2"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="flex space-x-3">
                      <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                        {{ t("common.queryKey") }}
                        <UITooltip trigger="hover" placement="top">
                          <template #trigger>
                            <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                          </template>
                          {{ t("common.querykeyTooltipMessage") }}
                        </UITooltip>
                      </div>
                    </div>
                    <div>
                      <UIInput
                        id="fieldQueryKey"
                        class="standard-input-ghl"
                        :model-value="getHiddenQuery"
                        @update:model-value="updateHiddenQuery"
                        @blur="store.addToHistory()"
                      ></UIInput>
                    </div>
                  </div>
                  <div class="form-check-req-hid">
                    <UICheckbox
                      id="fieldRequired"
                      :disabled="isRequiredCheckboxDisabled"
                      :checked="getRequired"
                      @update:checked="updateRequired"
                      >{{ t("common.required") }}</UICheckbox
                    >
                  </div>
                </div>
              </div>
              <div v-else-if="selectedElement.index === undefined">
                <h6 class="field-name-sidebar">
                  {{ selectedElementStore.slideName }}
                </h6>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("survey.slideName") }}</span
                    >
                  </div>
                  <UIInput
                    id="slideName"
                    class="standard-input-ghl"
                    :model-value="getSlideName"
                    @update:model-value="updateSlideName"
                    @blur="store.addToHistory()"
                  >
                  </UIInput>
                </div>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("survey.slidePosition") }}</span
                    >
                  </div>
                  <UIInputNumber
                    id="slidePosition"
                    :value="selectedElement.slideIndex + 1"
                    :disabled="true"
                    size="large"
                    :show-button="false"
                    class="standard-input-ghl"
                    @blur="store.addToHistory()"
                  ></UIInputNumber>
                </div>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("survey.jumpTo") }}</span
                    >
                  </div>
                  <UISelect
                    id="jump"
                    :value="selectedElementStore.jumpTo || ''"
                    :options="slideOptions"
                    :disabled="
                      selectedElement.slideIndex == store.slides.length - 1 ||
                      store.formAction.fieldSettingEnable
                    "
                    class="standard-input-ghl"
                    @update:value="setSlideJumpTo"
                    @change="store.addToHistory()"
                  ></UISelect>
                </div>

                <!-- Image Layout Settings for Slide -->
                <div class="form-group">
                  <SurveyImageLayoutSettings :selected-slide-index="selectedElement.slideIndex" />
                </div>
              </div>
              <div v-else-if="['date'].includes(dateElement.type)">
                <DateSettings
                  :date-element="dateElement"
                  :is-required-field-disable="isRequiredCheckboxDisabled"
                  :is-hidden-field-disable="isHiddenFieldDisable"
                  :is-custom-field-error="customFieldError"
                  @update-label="updateLabel"
                  @update-placeholder="updatePlaceholder"
                  @update-short-label="updateShortLabel"
                  @update-hidden-query="updateHiddenQuery"
                  @update-required="updateRequired"
                  @update-hidden="updateHidden"
                  @update-hidden-value="updateHiddenValue"
                  @update-date-format="updateDateFormat"
                  @update-date-separator="updateDateSeparator"
                  @update-date-disable-picker="updateDatedisablePicker"
                  @update-label-alignment="handleLabelAlignment"
                ></DateSettings>
                <div v-if="selectedElementStore?.model === 'opportunity'">
                  <UIDivider />
                  <div class="flex justify-center">
                    <UIButton
                      id="edit-opportunity-settings"
                      type="tertiary"
                      @click="store.showOpportunitiesModal = true"
                    >
                      <Edit01Icon class="mr-2 h-4 w-4" /> {{ t("common.editOpportunitySettings") }}
                    </UIButton>
                  </div>
                </div>
                <div v-if="isTrueCustomObjectFieldWrapper(selectedElementStore?.model)">
                  <UIDivider />
                  <div class="flex justify-center">
                    <UIButton
                      id="edit-custom-object-association"
                      type="tertiary"
                      @click="
                        store.currentCustomObjectModel = selectedElementStore?.model;
                        store.showContactAssociationModal = true;
                      "
                    >
                      <Edit01Icon class="mr-2 h-4 w-4" />
                      {{
                        t("common.editAssociationButton", {
                          edit: t("common.edit"),
                          objectName: getCustomObjectNamePluralWrapper(selectedElementStore?.model),
                          association: t("common.association"),
                        })
                      }}
                    </UIButton>
                  </div>
                </div>
                <EditOpportunitySettingsModal
                  :show="store.showOpportunitiesModal"
                  @update:success="saveOpportunitySettings"
                  @update:cancel="store.showOpportunitiesModal = false"
                />
              </div>
              <div v-else-if="selectedElementStore.tag === 'payment'">
                <PaymentSettings
                  :payment="selectedElementStore"
                  :selected-index="selectedElement.index"
                  :selected-slide-index="selectedElement.slideIndex"
                ></PaymentSettings>
              </div>
              <div v-else>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.fieldDescription") }}</span
                    >
                  </div>
                  <UIButton
                    id="fieldDescription"
                    type="tertiary"
                    class="standard-input-ghl w-[100%]"
                    @click="editDescription"
                    >{{ t("common.fieldDescription") }}</UIButton
                  >
                  <div>
                    <PopupRichTextEditor
                      v-if="showPopupRichTextEditorModal"
                      :model-active="showPopupRichTextEditorModal"
                      :code="selectedElementStore.description"
                      @update:show-model="showPopupRichTextEditorUpdate"
                      @update:submit="submitPopupRichTextEditorUpdate"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.label") }}</span
                    >
                  </div>
                  <UIInput
                    id="fieldTitle"
                    class="standard-input-ghl"
                    :model-value="getLabel"
                    :status="customFieldError ? 'error' : undefined"
                    @update:model-value="updateLabel"
                    @blur="store.addToHistory()"
                  >
                  </UIInput>
                  <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                    {{ t("common.customFieldExistsError") }}
                  </div>
                </div>
                <div class="form-group">
                  <LabelAlignment
                    :is-element-level="true"
                    :label-alignment="selectedElementStore.labelAlignment"
                    @update:label-alignment="handleLabelAlignment"
                  ></LabelAlignment>
                </div>
                <div
                  v-if="!['TEXTBOX_LIST'].includes(selectedElementStore?.dataType)"
                  class="form-group"
                >
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.placeholder") }}</span
                    >
                  </div>
                  <UIInput
                    id="fieldPlaceholder"
                    class="standard-input-ghl"
                    :model-value="getPlaceholder"
                    @update:model-value="updatePlaceholder"
                    @blur="store.addToHistory()"
                  ></UIInput>
                </div>
                <div
                  v-if="
                    !['image', 'header', 'button', 'terms_and_conditions'].includes(
                      selectedElementStore.tag
                    )
                  "
                  class="form-group"
                >
                  <div class="flex space-x-3">
                    <span class="switch-handle mb-1 block text-sm font-medium text-gray-700">{{
                      t("common.shortLabel")
                    }}</span>
                  </div>
                  <UIInput
                    id="shortLabel"
                    class="standard-input-ghl"
                    :model-value="getShortLabel"
                    @update:model-value="updateShortLabel"
                    @blur="store.addToHistory()"
                  >
                  </UIInput>
                </div>
                <div
                  v-if="
                    !['TEXTBOX_LIST', 'MULTIPLE_OPTIONS', 'FILE_UPLOAD', 'SIGNATURE'].includes(
                      selectedElementStore?.dataType
                    )
                  "
                  class="form-group"
                >
                  <div class="flex space-x-3">
                    <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                      {{ t("common.queryKey") }}
                      <UITooltip trigger="hover" placement="top">
                        <template #trigger>
                          <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                        </template>
                        {{ t("common.querykeyTooltipMessage") }}
                      </UITooltip>
                    </div>
                  </div>
                  <UIInput
                    id="fieldQueryKey"
                    class="standard-input-ghl"
                    :model-value="getHiddenQuery"
                    @update:model-value="updateHiddenQuery"
                    @blur="store.addToHistory()"
                  ></UIInput>
                </div>
                <div
                  v-if="
                    ['RADIO'].includes(selectedElementStore?.dataType) &&
                    selectedElementStore?.picklistOptionsImage?.length &&
                    !showPictureChoiceSettings
                  "
                  class="form-group mt-4"
                >
                  <div class="flex justify-between pb-2">
                    <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                      {{ t("common.spreadToColumns") }}
                    </div>
                    <UISwitch
                      :value="getSpreadColumns"
                      @update:value="updateSpreadColumns"
                    ></UISwitch>
                  </div>

                  <UIInputGroup v-if="getSpreadColumns">
                    <UIInputNumber
                      id="fieldQueryKey"
                      size="large"
                      class="w-100"
                      :min="1"
                      :max="10"
                      :default-value="getDefaultNumberOfColumns"
                      :show-button="false"
                      :model-value="getNumberOfColumns"
                      @update:model-value="updateNumberOfColumns"
                      @blur="store.addToHistory()"
                    ></UIInputNumber
                    ><UIInputGroupLabel>{{ t("common.columns") }}</UIInputGroupLabel>
                  </UIInputGroup>
                </div>
                <div class="form-check-req-hid">
                  <UICheckbox
                    id="fieldRequired"
                    :disabled="isRequiredCheckboxDisabled"
                    :checked="getRequired"
                    @update:checked="updateRequired"
                    >{{ t("common.required") }}</UICheckbox
                  >
                  <UICheckbox
                    v-if="
                      ![
                        'TEXTBOX_LIST',
                        'SINGLE_OPTIONS',
                        'MULTIPLE_OPTIONS',
                        'FILE_UPLOAD',
                        'SIGNATURE',
                      ].includes(selectedElementStore?.dataType)
                    "
                    id="fieldCheckbox"
                    :disabled="isHiddenFieldDisable"
                    :checked="getHidden"
                    @update:checked="updateHidden"
                    >{{ t("common.hidden") }}</UICheckbox
                  >
                </div>
                <div v-if="selectedElementStore.hidden" class="form-group hidden-field">
                  <div class="flex space-x-3">
                    <span
                      class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
                      >{{ t("common.hiddenValue") }}</span
                    >
                  </div>
                  <UIInput
                    id="fieldHiddenvalue"
                    class="standard-input-ghl"
                    :model-value="getHiddenValue"
                    @update:model-value="updateHiddenValue"
                    @blur="store.addToHistory()"
                  ></UIInput>
                </div>
                <div>
                  <UICheckbox
                    v-if="
                      selectedElementStore.tag == 'phone' ||
                      selectedElementStore.dataType == 'PHONE'
                    "
                    id="fieldCheckbox"
                    :checked="getEnableCountryPicker"
                    @update:checked="updateEnableCountryPicker"
                    >{{ t("common.enableCountryPicker") }}</UICheckbox
                  >
                </div>
                <div v-if="shouldShowMigrationWarning" class="pt-3">
                  <UIAlert id="warning-message-conditional-logic" type="warning">
                    <template #content>
                      {{ t("common.migrationWarningMessage") }}
                      <button
                        type="button"
                        class="ml-0.5 cursor-pointer border-0 bg-transparent p-0 text-blue-600 underline hover:text-blue-800"
                        @click="openConditionalLogicFromField"
                        ><br />
                        {{ t("form.openConditionalLogic") }}
                      </button>
                    </template>
                  </UIAlert>
                </div>
                <div
                  v-if="
                    ['RADIO', 'CHECKBOX', 'SINGLE_OPTIONS'].includes(
                      selectedElementStore.dataType
                    ) &&
                    (!store.builder.version || store.builder.version < 4)
                  "
                >
                  <Card id="logic-card" header="Logic">
                    <ol class="pl-4">
                      <li v-for="(option, index) in pickupFieldOptions" :key="option">
                        {{ option.label ? option.label : option }}
                        <div class="my-2">
                          <UISelect
                            :id="'radio' + index"
                            :value="fieldRadioActionModel(index)"
                            :options="radioOption"
                            class="standard-input-ghl"
                            @update:value="(value) => radioOptionSelection(value, option, index)"
                            @change="store.addToHistory()"
                          >
                            {{ t("common.data") }}
                          </UISelect>
                        </div>
                      </li>
                    </ol>
                  </Card>
                </div>
                <div v-if="selectedElementStore?.model === 'opportunity'">
                  <UIDivider />
                  <div class="flex justify-center">
                    <UIButton
                      id="edit-opportunity-settings"
                      type="tertiary"
                      @click="store.showOpportunitiesModal = true"
                    >
                      <Edit01Icon class="mr-2 h-4 w-4" />
                      {{ t("common.editOpportunitySettings") }}
                    </UIButton>
                  </div>
                  <p v-if="!store.opportunitySettings" class="pt-6">
                    <UIAlert id="warning-message" type="warning">
                      <template #content>
                        {{ t("common.opportunitySettingsWarning") }}
                      </template>
                    </UIAlert>
                  </p>
                </div>
                <div v-if="isTrueCustomObjectFieldWrapper(selectedElementStore?.model)">
                  <UIDivider />
                  <div class="flex justify-center">
                    <UIButton
                      id="edit-custom-object-association"
                      type="tertiary"
                      @click="
                        store.currentCustomObjectModel = selectedElementStore?.model;
                        store.showContactAssociationModal = true;
                      "
                    >
                      <Edit01Icon class="mr-2 h-4 w-4" />
                      {{
                        t("common.editAssociationButton", {
                          edit: t("common.edit"),
                          objectName: getCustomObjectNamePluralWrapper(selectedElementStore?.model),
                          association: t("common.association"),
                        })
                      }}
                    </UIButton>
                  </div>
                </div>
                <EditOpportunitySettingsModal
                  :show="store.showOpportunitiesModal"
                  @update:success="saveOpportunitySettings"
                  @update:cancel="store.showOpportunitiesModal = false"
                />
                <ContactAssociationSettingsModal
                  :show="store.showContactAssociationModal"
                  :object-model="store.currentCustomObjectModel"
                  @update:success="saveContactAssociationSettings"
                  @update:cancel="store.showContactAssociationModal = false"
                />
                <div v-if="selectedElementStore?.tag === 'email' && !selectedElementStore?.hidden">
                  <div class="mt-8 flex justify-between">
                    <span class="hl-text-input-label mb-1 block text-gray-700">
                      {{ t("common.validateEmailHeader") }}
                    </span>
                    <UICheckbox
                      id="email-checkbox"
                      v-model:checked="enableValidateEmailModel"
                      @update:checked="store.addToHistory"
                    />
                  </div>
                  <div class="text-xs text-gray-700">
                    {{ t("common.validateEmailDescription") }}
                  </div>
                </div>
                <div
                  v-if="
                    (selectedElementStore?.tag === 'phone' ||
                      selectedElementStore?.dataType === 'PHONE') &&
                    !selectedElementStore?.hidden
                  "
                >
                  <div class="mt-8 flex justify-between">
                    <span class="hl-text-input-label mb-1 block text-gray-700">
                      {{ t("common.validatePhoneHeader") }}
                    </span>
                    <UICheckbox
                      id="phone-checkbox"
                      v-model:checked="enableValidatePhoneModel"
                      @update:checked="store.addToHistory"
                    />
                  </div>
                  <div class="mb-4 text-xs text-gray-700">
                    {{ t("common.validatePhoneDescription") }}
                  </div>
                  <UIAlert id="warning-message" type="warning">
                    <template #content>
                      {{ t("common.validatePhoneWarning") }}
                    </template>
                  </UIAlert>
                </div>
              </div>
            </UICollapseItem>
            <PictureChoiceAltTextSettings
              v-if="showPictureChoiceSettings"
              :element="selectedElementStore"
              @update:element="updatePictureChoiceElement"
            />
            <UICollapseItem
              v-if="selectedElementStore?.dataType === 'URL'"
              id="url-field-settings"
              :title="t('common.urlField.settingsTitle')"
              :name="t('common.urlField.settingsTitle')"
            >
              <UrlFieldSettings
                :model-value="selectedElementStore?.urlValidation"
                @update:model-value="updateUrlValidation"
              />
            </UICollapseItem>
            <UICollapseItem
              v-if="!selectedElementStore?.standard && selectedElement.index !== undefined"
              id="advanced-settings"
              :title="t('common.advancedSettings')"
              :name="t('common.advancedSettings')"
            >
              <div>
                <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                  {{ t("common.customFieldName") }}
                  <UITooltip trigger="hover" placement="top">
                    <template #trigger>
                      <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                    </template>
                    <template #default>
                      <span>
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                        {{ t(customFieldNameTooltipKey) }}
                        <a
                          :href="customFieldNameTooltipLearnMoreUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-blue-500 underline hover:text-blue-700"
                        >
                          <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                          {{ t(customFieldNameTooltipLearnMoreKey) }}
                        </a>
                      </span>
                    </template>
                  </UITooltip>
                </div>
                <UIInput
                  id="custom-field-label"
                  class="standard-input-ghl"
                  :model-value="
                    selectedElementStore?.customFieldLabel ||
                    selectedElementStore?.name ||
                    selectedElementStore?.label
                  "
                  :disabled="selectedElementStore?.customEdited || !selectedElementStore?.custom"
                  :status="customFieldError ? 'error' : undefined"
                  @update:model-value="updateLabel"
                  @blur="store.addToHistory()"
                />
                <div v-if="customFieldError" class="mt-1 text-xs text-red-500">
                  {{ t("common.customFieldExistsError") }}
                </div>
                <div class="switch-handle mb-1 mt-6 block text-sm font-medium text-gray-700">
                  {{ t("common.uniqueKey") }}
                  <UITooltip trigger="hover" placement="top">
                    <template #trigger>
                      <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
                    </template>
                    <template #default>
                      <span>
                        {{ t("common.uniqueKeyTooltip") }}
                        <a
                          :href="customFieldNameTooltipLearnMoreUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="ml-1 text-blue-500 underline hover:text-blue-700"
                        >
                          {{ t("common.uniqueKeyTooltipLearnMore") }}
                        </a>
                      </span>
                    </template>
                  </UITooltip>
                </div>
                <UIInput
                  id="uniqueKey"
                  class="standard-input-ghl"
                  :model-value="getUniqueKey"
                  :disabled="disableUniqueKey"
                  :placeholder="uniqueKeyPlaceholder"
                  :status="uniqueKeyError ? 'error' : undefined"
                  @update:model-value="updateUniqueKey"
                  @blur="store.addToHistory()"
                />
                <div v-if="uniqueKeyError" class="mt-1 text-xs text-red-500">
                  {{ t("common.uniqueKeyNotUniqueError") }}
                </div>
              </div>
            </UICollapseItem>
          </UICollapse>
        </UITabPane>
        <UITabPane
          name="Options"
          :tab="
            selectedElementStore?.dataType === 'FILE_UPLOAD'
              ? t('common.uploadOptions')
              : selectedElementStore?.dataType === 'SCORE'
              ? t('common.calculations')
              : t('common.options')
          "
        >
          <PictureChoiceSettings
            v-if="showPictureChoiceSettings"
            :element="selectedElementStore"
            :media-center-sub-category="mediaCenterSubCategory"
            @update:element="updatePictureChoiceElement"
          />
          <ListEditor
            v-if="selectedElementStore?.dataType === 'TEXTBOX_LIST'"
            :key="`list-editor-${props.selectedElement.slideIndex}-${props.selectedElement.index}-${
              selectedElementStore?.picklistOptions?.length || 0
            }`"
            :picklist-options="selectedElementStore?.picklistOptions"
            :custom-field-created="
              selectedElementStore?.customEdited || !selectedElementStore?.custom
            "
            :hide-prefill-value="isCustomObjectFieldWrapper(selectedElementStore?.model)"
            @update:picklist-options="updatePicklistOptions"
          />
          <DropdownEditor
            v-if="
              !showPictureChoiceSettings &&
              ['SINGLE_OPTIONS', 'MULTIPLE_OPTIONS', 'CHECKBOX'].includes(
                selectedElementStore?.dataType
              )
            "
            :key="`dropdown-editor-${props.selectedElement.slideIndex}-${
              props.selectedElement.index
            }-${selectedElementStore?.enableCalculations ? 'calc' : 'normal'}-${
              selectedElementStore?.picklistOptions?.length || 0
            }`"
            :selected-element="selectedElementStore"
            :options="
              selectedElementStore?.enableCalculations
                ? selectedElementStore?.calculatedOptions || []
                : selectedElementStore?.picklistOptions
            "
            :enable-calculations="selectedElementStore?.enableCalculations"
            :hide-prefill-value="isCustomObjectFieldWrapper(selectedElementStore?.model)"
            :custom-field-created="
              selectedElementStore?.customEdited || !selectedElementStore?.custom
            "
            @update:options="updatePicklistOptions"
            @update:style="updateStyle"
            @update:allow-custom-option="updateAllowCustomOption"
          />
          <RadioSelectEditor
            v-if="
              !showPictureChoiceSettings &&
              selectedElementStore?.dataType === 'RADIO'
            "
            :key="`radio-editor-${props.selectedElement.slideIndex}-${
              props.selectedElement.index
            }-${selectedElementStore?.enableCalculations ? 'calc' : 'normal'}-${
              selectedElementStore?.picklistOptions?.length || 0
            }`"
            :selected-element="selectedElementStore"
            :options="
              selectedElementStore?.enableCalculations
                ? selectedElementStore?.calculatedOptions || []
                : selectedElementStore?.picklistOptions
            "
            :enable-calculations="selectedElementStore?.enableCalculations"
            :hide-prefill-value="isCustomObjectFieldWrapper(selectedElementStore?.model)"
            :custom-field-created="
              selectedElementStore?.customEdited || !selectedElementStore?.custom
            "
            @update:options="updatePicklistOptions"
            @update:style="updateStyle"
            @update:allow-custom-option="updateAllowCustomOption"
          />
          <ScoreEditor
            v-if="selectedElementStore?.dataType === 'SCORE'"
            :calculation="selectedElementStore?.calculation"
            @update:calculation="updateCalculation"
          />
          <FileUploadEditor
            v-if="selectedElementStore?.dataType === 'FILE_UPLOAD'"
            :options="selectedElementStore?.picklistOptions || ['all']"
            :show-file-access="supportsFileAccessChoice(selectedElementStore)"
            :is-private="isPrivateCustomField(selectedElementStore)"
            :file-access-locked="fileAccessLocked"
            @update:is-private="updateFileAccess"
            @update:options="updatePicklistOptions"
          />
          <div v-if="selectedElementStore?.dataType === 'FILE_UPLOAD'">
            <div class="my-4 flex items-center justify-between">
              <div class="my-auto text-sm font-normal text-gray-700">
                {{ t("common.allowMultipleFiles") }}
              </div>
              <UISwitch
                v-model:value="selectedElementStore.isMultipleFile"
                size="small"
                @update:value="() => store.markCustomFieldUpdated(selectedElementStore)"
              />
            </div>
            <div v-if="selectedElementStore.isMultipleFile">
              <div class="mb-2 mt-4 text-sm font-normal text-gray-700">
                {{ t("common.maxFileLimit") }}
              </div>
              <UIInput
                id="max-file-limit"
                v-model:value="selectedElementStore.maxFileSize"
                @update:value="() => store.markCustomFieldUpdated(selectedElementStore, false)"
                @blur="store.addToHistory()"
              />
            </div>
          </div>
          <div
            v-if="
              !showPictureChoiceSettings &&
              ['SINGLE_OPTIONS', 'MULTIPLE_OPTIONS', 'RADIO', 'CHECKBOX'].includes(
                selectedElementStore?.dataType
              ) && selectedElementStore?.model !== 'opportunity'
            "
            class="my-4 flex items-center justify-between"
          >
            <div class="my-auto block text-sm font-medium text-gray-700">
              {{ t("common.enableCalculations") }}
            </div>
            <UISwitch
              v-model:value="selectedElementStore.enableCalculations"
              @update:value="updateEnableCalculations"
            />
          </div>
          <div
            v-if="
              !showPictureChoiceSettings &&
              ['RADIO', 'CHECKBOX'].includes(selectedElementStore?.dataType)
            "
            class="form-group mt-4"
          >
            <div class="flex justify-between pb-2">
              <div class="switch-handle mb-1 block text-sm font-medium text-gray-700">
                {{ t("common.spreadToColumns") }}
              </div>
              <UISwitch :value="getSpreadColumns" @update:value="updateSpreadColumns"></UISwitch>
            </div>

            <UIInputGroup v-if="getSpreadColumns">
              <UIInputNumber
                id="fieldQueryKey"
                size="large"
                class="w-100"
                :min="1"
                :max="10"
                :default-value="getDefaultNumberOfColumns"
                :show-button="false"
                :model-value="getNumberOfColumns"
                @update:model-value="updateNumberOfColumns"
                @blur="store.addToHistory()"
              ></UIInputNumber
              ><UIInputGroupLabel>{{ t("common.columns") }}</UIInputGroupLabel>
            </UIInputGroup>
          </div>
          <UIAlert
            v-if="
              !showPictureChoiceSettings &&
              ['TEXTBOX_LIST', 'SINGLE_OPTIONS', 'MULTIPLE_OPTIONS', 'RADIO', 'CHECKBOX'].includes(
                selectedElementStore?.dataType
              ) &&
              (selectedElementStore?.customEdited || !selectedElementStore?.custom)
            "
            id="warning-message"
            type="warning"
          >
            <template #content>
              {{ t("common.swapWarningMessageSurvey") }}
            </template>
          </UIAlert>
        </UITabPane>
      </UITabs>
    </div>
    <ConditionalLogicModal
      :show="store.showConditionalLogicModal"
      @update:close-modal="store.showConditionalLogicModal = false"
    />
  </div>
</template>

<style>
.field-name-sidebar {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center !important;
  margin-bottom: 1rem !important;
}

#custom-sidebar-form {
  font-size: 14px;
}

.standard-input-ghl {
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.query-key {
  display: flex;
  align-items: baseline;
}

.form-group {
  margin-bottom: 1rem;
}

.hidden-field {
  margin-top: 1rem;
}

.form-check-req-hid {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.arrow-button {
  margin-bottom: 0px;
  cursor: pointer;
}

.alignment {
  display: flex;
  margin-bottom: 10px;
}

.alignment-div {
  margin-right: 10px;
}

.custom-icons {
  color: #38a0db;
  margin: 0 10px;
  cursor: pointer;
}

#hide-tabs > .n-tabs > .n-tabs-nav,
#hide-tabs > .n-tabs > .n-tabs-nav .n-tabs-nav-scroll-wrapper {
  display: none !important;
}

.date-time-sidebar-tabs > .n-tabs > .n-tabs-pane-wrapper > .n-tab-pane {
  padding-top: 0 !important;
}

#logic-card > div.p-3 {
  padding-bottom: 1px !important;
}
</style>

<style scoped>
/* Only our inline label overrides are scoped */
:deep(#imageLabelInline.n-input),
:deep(#htmlLabelInline.n-input) {
  border: 0 !important;
  box-shadow: none !important;
}
:deep(#imageLabelInline .n-input__border),
:deep(#imageLabelInline .n-input__state-border),
:deep(#htmlLabelInline .n-input__border),
:deep(#htmlLabelInline .n-input__state-border) {
  display: none !important;
}
:deep(#imageLabelInline .n-input__input-el),
:deep(#htmlLabelInline .n-input__input-el) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
